// Manual order of records (a card's place in a board column): a fractional key-string, so a move writes one record, never its neighbours.

/** The key of a record's place in its record data. Field keys are [a-z0-9_], so it never meets a field; only `record.move` writes it. */
export const RANK_KEY = '$rank';

const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const RANK_RE = /^[0-9A-Za-z]{1,64}$/;

/** A valid rank: base-62 digits that do not end with zero (a trailing zero would make two equal places). */
export const isRank = (v: unknown): v is string => typeof v === 'string' && RANK_RE.test(v) && !v.endsWith('0');

/** The rank of a record, or null when it has no manual place yet. */
export const rankOf = (data: Record<string, unknown>): string | null => (isRank(data[RANK_KEY]) ? data[RANK_KEY] : null);

/** Midpoint of two fractions written as digit strings: `a` < `b`; '' is zero, null is one. */
function midpoint(a: string, b: string | null): string {
  if (b !== null) {
    let n = 0;
    while ((a[n] ?? '0') === b[n]) n++;
    if (n > 0) return b.slice(0, n) + midpoint(a.slice(n), b.slice(n));
  }
  const da = a ? DIGITS.indexOf(a[0]!) : 0;
  const db = b !== null ? DIGITS.indexOf(b[0]!) : DIGITS.length;
  if (db - da < 1) throw new Error('rank order');
  if (db - da > 1) return DIGITS[Math.round((da + db) / 2)]!;
  // Neighbouring digits: a longer `b` has room right at its first digit; otherwise go one digit deeper after `a`.
  if (b !== null && b.length > 1) return b.slice(0, 1);
  return DIGITS[da]! + midpoint(a.slice(1), null);
}

/** A rank strictly between two places; null ends mean the start or the end of the column. */
export function rankBetween(before: string | null, after: string | null): string {
  if (before !== null && after !== null && before >= after) throw new Error('rank order');
  return midpoint(before ?? '', after);
}

/** `n` ranks in a row strictly between two places, spread by halving so they stay short (cards that had no place get one). */
export function ranksBetween(before: string | null, after: string | null, n: number): string[] {
  if (n <= 0) return [];
  const mid = rankBetween(before, after);
  const left = Math.floor((n - 1) / 2);
  return [...ranksBetween(before, mid, left), mid, ...ranksBetween(mid, after, n - 1 - left)];
}
